import { css } from '@emotion/react'
import Link from 'next/link'
import { colors } from 'plume-ui'
import { useState } from 'react'
import { Dispatch } from 'redux'
import { A } from '@/components/atoms/A'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { Snackbar } from '@/components/atoms/Snackbar'
import { Modal, ModalCloseButton, ModalTitle } from '@/components/atoms/Modal'
import { clearAll, reset, toggleColumn } from '../../actions/column'
import { updateColumns } from '../../actions/index'
import { PermissionState } from '../../reducers/permission'
import { UNREMOVABLE_NAMES, getColumnNames } from '../../reducers/column'
import { ColumnSort } from '../column-sort'
import { HeaderButton } from '../header-button'

type Props = {
  dispatch: Dispatch
  selectedColumns: string[]
  permission: PermissionState
  isHidden: boolean
  onOpen: () => void
  onClose: () => void
}

export const ColumnSetting: React.FC<Props> = (props) => {
  const [isShow, setIsShow] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')

  const handleToggle = (name: string) => {
    props.dispatch(toggleColumn(name))
  }

  const handleSort = (names: string[]) => {
    props.dispatch(updateColumns(names))
  }

  // TODO: 親側のコンポーネントでも同じような情報を持っているので整理したい
  const availableColumnNamesHash = getColumnNames(
    props.permission.latestPlan,
    props.permission.earlyAccess
  )
  const initialColumnNames = Object.keys(availableColumnNamesHash)
    .filter((key) => {
      // @ts-ignore
      return availableColumnNamesHash[key].selected
    })
    .sort(
      (a: string, b: string) =>
        // @ts-ignore
        availableColumnNamesHash[a].displayOrder -
        // @ts-ignore
        availableColumnNamesHash[b].displayOrder
    )

  const handleSelectAll = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()
    // displayOrder が小さい順に key を並び替えた key の配列を返す
    const sortedKeys = Object.entries(availableColumnNamesHash)
      .sort(([, a], [, b]) => a.displayOrder - b.displayOrder)
      .map(([key]) => key)
    props.dispatch(updateColumns(sortedKeys))
  }

  const handleClearAll = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()
    props.dispatch(clearAll())
  }

  const handleReset = (initialColumnNames: string[]) => {
    props.dispatch(reset(initialColumnNames))
  }

  const handleSaveColumnSetting = (names: string[]) => {
    try {
      localStorage.setItem('column_setting', names.toString())
      setSnackbarMessage('表示設定を保存しました。')
    } catch (e) {
      setSnackbarMessage('表示設定の保存に失敗しました。')
    }
    setIsShow(true)
  }

  const selected = props.selectedColumns

  return (
    <>
      <HeaderButton
        isLoading={false}
        disabled={!props.isHidden}
        label={'表示設定'}
        onClick={props.onOpen}
      >
        <IcomoonIcon
          name="gear"
          css={css`
            color: ${colors.black10};
            vertical-align: middle;
            padding-right: 4px;
            font-size: 20px;
          `}
        />
      </HeaderButton>
      <Modal isHidden={props.isHidden} onClose={props.onClose}>
        <div
          className="column-setting"
          css={css`
            background-color: ${colors.white00};
          `}
        >
          <div
            className="header"
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: flex-end;
            `}
          >
            <ModalTitle data-test-id="title">表示項目の選択</ModalTitle>
            <ModalCloseButton data-test-id="close" onClick={props.onClose} />
          </div>
          <div
            className="bodies"
            css={css`
              display: flex;
              flex-direction: row;
              & > .body {
                text-align: left;
              }
            `}
          >
            <div
              className="body column-selection"
              css={css`
                float: left;
                padding: 15px 15px 0 0;
              `}
            >
              <span
                className="description"
                css={css`
                  color: ${colors.gray90};
                  font-size: 13px;
                `}
              >
                表示項目の選択
              </span>
              <Link href="/companies#" passHref>
                <a
                  onClick={handleSelectAll.bind(this)}
                  css={css`
                    margin-left: 15px;
                    color: ${colors.link};
                    text-decoration: none;
                    font-size: 13px;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                >
                  すべて選択
                </a>
              </Link>
              <Link href="/companies#" passHref>
                <a
                  onClick={handleClearAll.bind(this)}
                  css={css`
                    margin-left: 15px;
                    color: ${colors.link};
                    text-decoration: none;
                    font-size: 13px;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                >
                  すべてクリア
                </a>
              </Link>
              <ul
                className="options"
                css={css`
                  background-color: #fff;
                  border: 1px solid #d9d9d9;
                  border-radius: 2px;
                  overflow-y: auto;
                  margin-top: 6px;
                  width: 400px;
                  height: 345px;
                  padding-left: 0;
                `}
              >
                {Object.keys(availableColumnNamesHash)
                  .sort(
                    (a: string, b: string) =>
                      // @ts-ignore
                      availableColumnNamesHash[a].displayOrder -
                      // @ts-ignore
                      availableColumnNamesHash[b].displayOrder
                  )
                  .map((name: string, i: any) => (
                    <li
                      key={`${i}-${name}`}
                      css={css`
                        padding: 5px 0 5px 15px;
                      `}
                    >
                      <label className="checkbox">
                        <input
                          className="checkbox"
                          disabled={UNREMOVABLE_NAMES.includes(name)}
                          type="checkbox"
                          checked={selected.includes(name)} //? 'checked' : undefined}
                          onChange={() => handleToggle(name)}
                          css={css`
                            cursor: pointer;
                            margin: 0 10px 0 0;
                            vertical-align: middle;
                          `}
                        />
                        {
                          // @ts-ignore
                          availableColumnNamesHash[name].label.replace('\n', '')
                        }
                      </label>
                    </li>
                  ))}
              </ul>
            </div>
            <ColumnSort
              selected={selected}
              onRemove={(name) => handleToggle(name)}
              onSort={(selected) => handleSort(selected)}
            />
          </div>
          <div
            className="reset"
            css={css`
              padding-bottom: 15px;
              padding-left: 15px;
              text-align: right;
            `}
          >
            <A
              href="#"
              decoration="none"
              onClick={(e) => {
                e.preventDefault()
                handleReset(initialColumnNames)
              }}
              className="return-default"
              css={css`
                padding: 0px 6px 2px 0px;
                margin-right: 16px;
                font-size: 14px;
              `}
            >
              初期設定に戻す
            </A>
            <A
              href="#"
              onClick={(e) => {
                e.preventDefault()
                handleSaveColumnSetting(selected)
              }}
              decoration="none"
              className="save-column-setting"
              css={css`
                padding-bottom: 2px;
                font-size: 14px;
              `}
            >
              この設定を次からも使う
            </A>
          </div>
        </div>
      </Modal>
      {isShow && (
        <Snackbar
          setIsShow={setIsShow}
          isShow={isShow}
          text={snackbarMessage}
        />
      )}
    </>
  )
}
